import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Camera, CameraOff, RefreshCw } from 'lucide-react';
import {
  BrowserMultiFormatReader,
  BarcodeFormat,
  DecodeHintType,
  Result,
} from '@zxing/library';

type Props = {
  onResult?: (text: string, raw?: Result) => void;
  onError?: (message: string) => void;
  stopAfterScan?: boolean;
};

const QRScanner: React.FC<Props> = ({
  onResult,
  onError,
  stopAfterScan = true,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const [facing, setFacing] = useState<'user' | 'environment'>('environment');
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [nonce, setNonce] = useState(0);

  const chooseDeviceId = async (
    desired: 'user' | 'environment'
  ): Promise<string | undefined> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videos = devices.filter(d => d.kind === 'videoinput');
      if (videos.length === 0) return undefined;

      const isBack = (label: string) =>
        /(back|rear|environment)/i.test(label);
      const isFront = (label: string) =>
        /(front|user|face|webcam)/i.test(label);

      if (desired === 'environment') {
        const back = videos.find(v => isBack(v.label));
        if (back?.deviceId) return back.deviceId;
      } else {
        const front = videos.find(v => isFront(v.label));
        if (front?.deviceId) return front.deviceId;
      }
      return desired === 'environment'
        ? videos[videos.length - 1]?.deviceId
        : videos[0]?.deviceId;
    } catch {
      return undefined;
    }
  };

  const hints = useMemo(() => {
    const h = new Map<DecodeHintType, any>();
    h.set(DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.QR_CODE]);
    h.set(DecodeHintType.TRY_HARDER, true);
    return h;
  }, []);

  const ensureReader = () => {
    if (!readerRef.current) {
      readerRef.current = new BrowserMultiFormatReader(hints, 300);
    }
    return readerRef.current;
  };

  const stop = () => {
    try {
      readerRef.current?.reset();
    } catch {
      // ignore
    }
    const v = videoRef.current;
    const stream = v?.srcObject as MediaStream | null;
    stream?.getTracks().forEach(t => t.stop());
    if (v) {
      v.srcObject = null;
    }
  };

  useEffect(() => {
    let cancelled = false;
    const start = async () => {
      setStarting(true);
      setPermissionError(null);
      const reader = ensureReader();
      const video = videoRef.current;
      if (!video) return;

      // Prefer an explicit device if chosen, else default to the front (user) camera.
      const callback = (result: Result | undefined, err: unknown) => {
        if (cancelled) return;
        if (result) {
          onResult?.(result.getText(), result);
          if (stopAfterScan) stop();
          return;
        }
        if (err) {
          const name = (err as any)?.name || '';
          const msg = (err as Error)?.message ?? String(err);
          // Ignore common transient decode errors
          const transient =
            name === 'NotFoundException' ||
            name === 'ChecksumException' ||
            name === 'FormatException' ||
            /No\s*MultiFormat/i.test(msg) ||
            /source width is 0/i.test(msg);
          if (!transient) {
            onError?.(msg);
          }
        }
      };

      const constraintsBase = {
        width: { ideal: 1280 },
        height: { ideal: 720 },
      } as const;

      try {
        const preflight = await navigator.mediaDevices.getUserMedia({
          video: { ...constraintsBase, facingMode: facing },
          audio: false,
        });
        preflight.getTracks().forEach(t => t.stop());
      } catch (e) {
        if (cancelled) return;
        const name = (e as any)?.name || '';
        const msg =
          name === 'NotAllowedError'
            ? 'Camera permission denied. Please allow camera access.'
            : (e as Error)?.message ?? String(e);
        setPermissionError(msg);
        onError?.(msg);
        setStarting(false);
        return;
      }

      try {
        const deviceId = await chooseDeviceId(facing);
        await reader.decodeFromConstraints(
          {
            video: {
              ...constraintsBase,
              ...(deviceId
                ? { deviceId: { exact: deviceId } }
                : { facingMode: facing }),
            },
            audio: false,
          },
          video,
          callback
        );
      } catch (e) {
        if (facing === 'environment') {
          try {
            const deviceId = await chooseDeviceId('user');
            await reader.decodeFromConstraints(
              {
                video: {
                  ...constraintsBase,
                  ...(deviceId
                    ? { deviceId: { exact: deviceId } }
                    : { facingMode: 'user' }),
                },
                audio: false,
              },
              video,
              callback
            );
            setFacing('user');
            setStarting(false);
            return;
          } catch (ee) {
            const msg = (ee as Error)?.message ?? String(ee);
            onError?.(msg);
          }
        } else {
          const msg = (e as Error)?.message ?? String(e);
          onError?.(msg);
        }
      } finally {
        setStarting(false);
      }
    };

    start();
    return () => {
      cancelled = true;
      stop();
    };
  }, [stopAfterScan, facing, nonce]);

  const restart = () => setNonce(n => n + 1);

  return (
    <div className="flex flex-col items-center justify-center relative">
      <p className="text-white w-[300px] text-center mb-[124px]">
        Make sure you focus on the QR code
      </p>
      {/* Camera switch button */}
      <button
        type="button"
        onClick={() => setFacing(prev => (prev === 'user' ? 'environment' : 'user'))}
        className="absolute top-2 right-2 z-10 bg-white/90 text-black p-2 rounded-full shadow cursor-pointer"
        aria-label="Switch camera"
        title={facing === 'user' ? 'Switch to back camera' : 'Switch to front camera'}
        disabled={starting}
      >
        <RefreshCw size={18} />
      </button>

      {/* Permission overlay */}
      {permissionError && (
        <div className="absolute inset-x-0 top-10 z-10 mx-auto w-[90%] max-w-sm bg-white text-black p-3 rounded shadow flex items-center gap-2">
          <CameraOff size={18} className="text-red-600" />
          <span className="text-xs flex-1">{permissionError}</span>
          <button
            type="button"
            onClick={restart}
            className="ml-2 bg-black text-white text-xs px-2 py-1 rounded inline-flex items-center gap-1"
          >
            <Camera size={14} /> Enable
          </button>
        </div>
      )}
      <video
        ref={videoRef}
        width={640}
        height={480}
        style={{
          width: '100%',
          maxWidth: 480,
          background: '#000',
          transform: facing === 'user' ? 'scaleX(-1)' : 'none',
        }}
        muted
        playsInline
        autoPlay
      />

      {starting && !permissionError && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded">
          Starting camera…
        </div>
      )}
    </div>
  );
};

export default QRScanner;
